/**
 * TimeAndMaterialsPage — v1.52.10
 *
 * The job Time & Materials workspace, merged in from the standalone
 * proto-time-and-materials prototype (frozen at v0.1.7 as the design spec).
 *
 * Three cascade sections against a selected job:
 *   1. Timesheets      — labour cost lives here; copy sell-side to billables.
 *   2. Billable items  — Items group (editable) + Purchase orders group
 *                        (REAL POs from DataContext, keyed by jobRef, with live
 *                        reconciliation state derived from linked Bills).
 *   3. Job P&L         — cost / charge / GST / margin rollup.
 *
 * Design decisions carried over from the prototype (v0.1.7):
 *   - ONE number per altitude: line → group header amounts → section cascade
 *     header. No subtotal rows, no section footers.
 *   - Cascade header totals are ALWAYS visible; muted while expanded.
 *   - Action toolbar sits directly below the table.
 *   - Unreconciled billable POs hard-block "+ Sales Document" (invoice).
 *
 * Reconciliation rule: a PO is reconciled when at least one linked Bill has
 * reconciledReceiptsSynced, or is Approved / Paid.
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  Paper, Stack, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { PurchaseOrder, TaxMode, InvoiceLineDetail } from '../data/types';
import { BRAND, STATUS_TONE, getStatusTone } from '../theme/tokens';
import { TM, PricingMode } from '../theme/timeAndMaterialsTokens';
import { JobEditChrome } from '../components/timeAndMaterials/JobEditChrome';
import { TmCollapsibleSection } from '../components/timeAndMaterials/TmCollapsibleSection';
import { TmCascadeSummary } from '../components/timeAndMaterials/TmCascadeSummary';
import { TmCellInput } from '../components/timeAndMaterials/TmCellInput';
import { RaiseJobPoDialog } from '../components/timeAndMaterials/RaiseJobPoDialog';
import { TmActionsBar } from '../components/timeAndMaterials/TmActionsBar';
import { InventorySettingsDrawer } from '../components/timeAndMaterials/InventorySettingsDrawer';
import { TmInfoModal, TmQ, type TmInfoEntry } from '../components/timeAndMaterials/TmInfoModal';
import { TmColHead } from '../components/timeAndMaterials/TmColHead';
import { TS_COL_INFO, BI_COL_INFO } from '../components/timeAndMaterials/timeAndMaterialsColumnInfo';
import { TM_EX, fmtAud } from '../components/timeAndMaterials/timeAndMaterialsExamples';
import '../styles/timeAndMaterials.css';

const fmt = (n: number) =>
  '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Local line models (prototype parity — component state only) ──────────────
interface TsRow {
  id: number;
  staff: string;
  serviceCode: string;
  date: string;
  start: string;
  end: string;
  hours: number;        // total worked hrs (cost side)
  billableHrs: number;  // hrs we charge
  costRate: number;
  chargeRate: number;
  copy: boolean;        // selected for "Copy to billable"
}

// Not transactional inventory: Required/Actual qty and unit prices are plain
// inline-editable cells (no pencil affordance). Cost total and Amount are
// DERIVED (actual qty × unit price) — never entered.
interface ItemLine {
  id: number;
  kind: 'Product' | 'Service' | 'Expense';
  code: string;
  description: string;
  reqQty: number;     // planned
  actualQty: number;  // drives all derived totals + invoicing
  cost: number;       // unit cost ex GST
  sell: number;       // unit sell ex GST
  billable: boolean;
  /** Service line mirrored from timesheets — labour cost lives in Timesheets, not here */
  tsLinked?: boolean;
  /** Timesheet-backed labour cost for this line (total hrs × cost rate), display only */
  tsCost?: number;
}

const KIND_CLASS: Record<ItemLine['kind'], string> = {
  Product: 'prod', Service: 'serv', Expense: 'exp',
};

const ITEM_KINDS: ItemLine['kind'][] = ['Service', 'Product', 'Expense'];

// ── Info dictionary (apply-tooltip pattern: Description · Example · Formula · Source) ──
const INFO: Record<string, TmInfoEntry> = {
  biitems: {
    t: 'Items (Product · Service · Expense)',
    ctx: 'Billable items · group',
    d: 'Editable billable lines you add directly to the job — stock drawn from the van or warehouse, labour service lines copied from timesheets, and recharged expenses. The Cost header includes labour cost mirrored from Timesheets (shown as "See timesheets" on the row); Job P&L counts that labour once, under Labour.',
    e: `Copy ${TM_EX.srBillHrs.toFixed(2)} billable hrs from timesheets → ${TM_EX.srCode} at ${fmtAud(TM_EX.srServiceHr)}/hr on ${TM_EX.jobRef} (${TM_EX.jobTitle}).`,
    f: 'Sale = Σ (actual qty × sales ex GST) over billable lines\nCost = Σ (materials/expense cost) + Σ (mirrored timesheet labour cost)',
    s: 'Billable items workspace rows · Service lines from Timesheets copy · Products from inventory price book.',
  },
  bipo: {
    t: 'Purchase orders',
    ctx: 'Billable items · group',
    d: 'Read-only lines sourced from the Purchase Orders module for this job. Cost comes from the PO; sale is cost marked up by the supplier default. Manage the PO itself in Purchase Orders.',
    e: `${TM_EX.poId} — ${TM_EX.poDesc} (${fmtAud(TM_EX.poExGst)} ex-GST from ${TM_EX.poSupplier}) recharged at ${TM_EX.poMarkupPct}% markup on ${TM_EX.jobRef}.`,
    f: 'Sale = PO ex-GST cost × (1 + supplier default markup %)',
    s: 'Purchase Orders → PO records WHERE jobRef = this job · PO.exGst + Supplier.defaultMarkup.',
  },
  unrecon: {
    t: 'Not reconciled',
    ctx: 'Purchase order · status',
    d: 'This PO has no approved supplier bill reconciled against it — the cost shown is committed, not confirmed. Invoicing this line now risks charging the customer against a cost that may change.',
    e: `${TM_EX.poId} (${TM_EX.poSupplier}, Submitted) — supplier bill not reconciled yet → ${fmtAud(TM_EX.poExGst)} cost still an estimate.`,
    f: 'Reconciled = ∃ linked Bill WHERE status ∈ [Approved, Paid] OR receipts synced',
    s: 'Purchase Orders → PO status + Bills linked to PO.id · reconciliation flag on Bill.',
  },
};

// ── Cascade section shell ─────────────────────────────────────────────────────
// DRY rule (Meridian · Step 3): a number appears in exactly ONE place per state.
//   Collapsed → totals live in the header bar (only thing visible).
//   Expanded  → header goes quiet; the single subtotal renders at the bottom.
// Group header row (purple, amounts on the right — prototype .grp-head)
const GroupHead: React.FC<{
  label: string;
  infoKey: string;
  cost: number;
  sale: number;
  colSpan: number;
  onInfo: (key: string) => void;
}> = ({ label, infoKey, cost, sale, colSpan, onInfo }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ backgroundColor: TM.purpleSoft, py: 0.7,
      color: TM.purpleDeep, fontWeight: 800, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
      <TmQ onClick={() => onInfo(infoKey)} />
    </TableCell>
    <TableCell align="right" colSpan={2} sx={{ backgroundColor: TM.purpleSoft, py: 0.7, whiteSpace: 'nowrap' }}>
      <TmCascadeSummary cost={cost} sale={sale} taxTag="ex GST" className="tm-grp-amts" />
    </TableCell>
  </TableRow>
);

// ── Page ──────────────────────────────────────────────────────────────────────
export const TimeAndMaterialsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const {
    jobs, purchaseOrders, bills, suppliers, invoices,
    createInvoice, updateInvoice, updateInvoiceLineDetails,
  } = useData();

  // Job options: real Jobs + any PO jobRefs without a Job entity, so every
  // seeded PO is reachable from day one of the merge.
  const jobOptions = useMemo(() => {
    const opts = jobs.map(j => ({ ref: j.jobRef, label: `${j.jobRef} — ${j.title}`, job: j }));
    const known = new Set(jobs.map(j => j.jobRef));
    const poRefs = Array.from(new Set(purchaseOrders.map(p => p.jobRef))).filter(r => r && !known.has(r));
    poRefs.sort();
    return [...opts, ...poRefs.map(r => ({ ref: r, label: `${r} — (from purchase orders)`, job: undefined as any }))];
  }, [jobs, purchaseOrders]);

  const defaultRef = useMemo(() => {
    const withPo = jobOptions.find(o => purchaseOrders.some(p => p.jobRef === o.ref && p.status !== 'Draft' && p.status !== 'Cancelled'));
    return (withPo ?? jobOptions[0])?.ref ?? '';
  }, [jobOptions, purchaseOrders]);

  const jobRef = params.get('job') ?? defaultRef;
  const setJobRef = (r: string) => setParams({ job: r });
  const job = jobs.find(j => j.jobRef === jobRef);

  // Tax modes (labels on cascade bars follow these — prototype v0.1.5)
  const [purchTax, setPurchTax] = useState<TaxMode>('Exclusive');
  const [salesTax, setSalesTax] = useState<TaxMode>('Exclusive');
  const [pricingMode, setPricingMode] = useState<PricingMode>('Do & Charge');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [showPosInline, setShowPosInline] = useState(true);
  const [salesMenuAnchor, setSalesMenuAnchor] = useState<HTMLElement | null>(null);
  const [sectionHeading, setSectionHeading] = useState('');
  const purchTag = purchTax === 'Inclusive' ? 'inc GST' : 'ex GST';
  const salesTag = salesTax === 'Inclusive' ? 'inc GST' : 'ex GST';

  // ── Timesheets (local rows, seeded per job) ────────────────────────────────
  const seedTs = useCallback((): TsRow[] => {
    const tech = job?.technician || 'Paul Tyrrell';
    const hrs = job?.timesheetHours || 3;
    const date = job?.visitDate || new Date().toLocaleDateString('en-AU');
    return [
      { id: 1, staff: tech, serviceCode: 'HVAC-SR', date, start: job?.startTime || '8 AM', end: job?.endTime || '11 AM',
        hours: hrs, billableHrs: hrs, costRate: 65, chargeRate: 150, copy: true },
      { id: 2, staff: 'Sam Nolo', serviceCode: 'HVAC-APP', date, start: '8 AM', end: '11 AM',
        hours: Math.max(1, Math.round(hrs * 0.75 * 4) / 4), billableHrs: Math.max(0.5, Math.round(hrs * 0.5 * 4) / 4),
        costRate: 45, chargeRate: 95, copy: false },
    ];
  }, [job]);

  const [tsRowsByJob, setTsRowsByJob] = useState<Record<string, TsRow[]>>({});
  const tsRows = tsRowsByJob[jobRef] ?? seedTs();
  const setTsRows = (rows: TsRow[]) => setTsRowsByJob(prev => ({ ...prev, [jobRef]: rows }));

  // ── Billable items (local rows per job) ────────────────────────────────────
  const seedItems = useCallback((): ItemLine[] => [
    {
      id: 1, kind: 'Service', code: TM_EX.srCode,
      description: `${TM_EX.techLead} — Daikin VRV annual service labour`,
      reqQty: TM_EX.srBillHrs, actualQty: TM_EX.srBillHrs, cost: 0, sell: TM_EX.srServiceHr, billable: true,
      tsLinked: true, tsCost: TM_EX.srTotalHrs * TM_EX.srCostHr,
    },
    {
      id: 2, kind: 'Product', code: TM_EX.thrmCode,
      description: TM_EX.thrmDesc,
      reqQty: 1, actualQty: 1, cost: TM_EX.thrmCost, sell: TM_EX.thrmSell, billable: true,
    },
    {
      id: 3, kind: 'Expense', code: TM_EX.calloutCode,
      description: TM_EX.calloutDesc,
      reqQty: 1, actualQty: 1, cost: 0, sell: TM_EX.calloutSell, billable: true,
    },
  ], []);

  const [itemsByJob, setItemsByJob] = useState<Record<string, ItemLine[]>>({});
  const items = itemsByJob[jobRef] ?? seedItems();
  const setItems = (rows: ItemLine[]) => setItemsByJob(prev => ({ ...prev, [jobRef]: rows }));

  // Billable toggle for PO lines (default: billable)
  const [poBillable, setPoBillable] = useState<Record<string, boolean>>({});

  // ── Real POs for this job + live reconciliation state ─────────────────────
  const jobPOs = useMemo(
    () => purchaseOrders.filter(p => p.jobRef === jobRef && p.status !== 'Draft' && p.status !== 'Cancelled'),
    [purchaseOrders, jobRef],
  );

  const isReconciled = useCallback((po: PurchaseOrder) =>
    bills.some(b => b.linkedPoId === po.id &&
      (b.reconciledReceiptsSynced || b.status === 'Approved' || b.status === 'Paid')),
    [bills]);

  const poSell = useCallback((po: PurchaseOrder) => {
    const markup = suppliers.find(s => s.id === po.supplierId)?.defaultMarkup ?? 20;
    return Math.round(po.exGst * (1 + markup / 100) * 100) / 100;
  }, [suppliers]);

  // ── Rollups (one number per altitude) ──────────────────────────────────────
  const tsCost = tsRows.reduce((s, r) => s + r.hours * r.costRate, 0);
  const tsSale = tsRows.reduce((s, r) => s + r.billableHrs * r.chargeRate, 0);

  const itemsCost = items.reduce((s, l) => s + l.actualQty * l.cost, 0);
  const itemsSale = items.reduce((s, l) => s + (l.billable ? l.actualQty * l.sell : 0), 0);
  // Labour cost mirrored from Timesheets on copied Service lines — shown in the
  // Items section (like the original prototype) but counted once in P&L, from Timesheets.
  const itemsLabourCost = items.reduce((s, l) => s + (l.tsLinked ? (l.tsCost ?? 0) : 0), 0);
  const itemsCostDisplay = itemsCost + itemsLabourCost;

  const posCost = jobPOs.reduce((s, p) => s + p.exGst, 0);
  const posSale = jobPOs.reduce((s, p) => s + ((poBillable[p.id] ?? true) ? poSell(p) : 0), 0);

  const biCost = itemsCost + posCost;
  const biSale = itemsSale + posSale;
  const biCostDisplay = itemsCostDisplay + posCost;

  const plCost = tsCost + biCost;
  const plCharge = biSale;
  const gst = Math.round(plCharge * 0.1 * 100) / 100;
  const totalInc = Math.round((plCharge + gst) * 100) / 100;
  const margin = Math.round((plCharge - plCost) * 100) / 100;
  const marginPct = plCharge > 0 ? (margin / plCharge) * 100 : 0;

  // Invoiced to date for this job — invoices raised from this page carry the
  // jobRef in purchaseOrderNumber. WIP is what's billable but not yet invoiced.
  const jobInvoices = invoices.filter(i => i.purchaseOrderNumber === jobRef && i.status !== 'Void');
  const invoicedToDate = jobInvoices.reduce((s, i) => s + i.exGst, 0);
  const wip = Math.max(0, Math.round((plCharge - invoicedToDate) * 100) / 100);

  const totalHrs = tsRows.reduce((s, r) => s + r.hours, 0);
  const totalBillableHrs = tsRows.reduce((s, r) => s + r.billableHrs, 0);

  // P&L breakdown figures (prototype v0.1.7 footer layout — restored on request)
  const labourCharge = items.filter(l => l.kind === 'Service' && l.billable)
    .reduce((s, l) => s + l.actualQty * l.sell, 0);
  const materialsCharge = itemsSale - labourCharge;
  const committedPO = jobPOs.filter(p => !isReconciled(p)).reduce((s, p) => s + p.exGst, 0);
  const wipCostBacking = plCost - committedPO;
  const leak = tsRows.reduce((s, r) => s + Math.max(0, r.hours - r.billableHrs) * r.costRate, 0);
  const markupPct = plCost > 0 ? (margin / plCost) * 100 : 0;

  // ── P&L explainers — explicit, built from THIS job's live numbers ─────────
  // (apply-tooltip pattern: Description · Example · Formula)
  const PL_INFO: Record<string, TmInfoEntry> = {
    wip: {
      t: 'Work in progress (WIP)',
      ctx: 'Job P&L',
      d: 'Work done on this job that you have not invoiced yet. Sales side: timesheet billable hours \u00d7 service rate (once copied to billable), plus PO recharges and material sales. Then subtract what is already on an invoice for this job. (Cost backing uses timesheet total hours — see Cost backing this WIP.)',
      e: `Items ${fmt(itemsSale)} (incl. copied labour) + PO recharge ${fmt(posSale)} \u2212 invoiced to date ${fmt(invoicedToDate)} = ${fmt(wip)} still to bill on ${jobRef}.`,
      f: 'WIP = \u03a3(timesheet billable hours \u00d7 service rate)\n  + \u03a3(PO ex-GST \u00d7 (1 + markup %))\n  + \u03a3(actual qty \u00d7 sales price) \u2212 invoiced to date',
      s: 'Sales: Billable items Amount + PO sales on job ' + jobRef + ' \u2212 Invoices linked to this job ref.',
    },
    cost: {
      t: 'What it costs',
      ctx: 'Job P&L',
      d: 'What this job costs you, before any markup. Labour uses timesheet total hours (all hours worked, not just billable) \u00d7 service item cost rate. Plus PO ex-GST cost and materials at actual qty \u00d7 unit cost.',
      e: `Timesheets ${totalHrs.toFixed(2)} timesheet total hours \u00d7 cost rates = ${fmt(tsCost)} + POs ${fmt(posCost)} + materials ${fmt(itemsCost)} = ${fmt(plCost)}.`,
      f: 'Cost = \u03a3(timesheet total hours \u00d7 service item cost rate)\n  + \u03a3(PO ex-GST) + \u03a3(actual qty \u00d7 unit cost)\n\n\u26a0 Labour cost uses total hours, not billable hours.',
      s: 'Labour: Timesheets \u2192 Timesheet.totalHours \u00d7 Service item.costRate.\nMaterials: Billable items \u2192 actualQty \u00d7 Cost (Ex).\nPOs: Purchase Orders \u2192 PO.exGst for job ' + jobRef + '.',
    },
    charge: {
      t: 'What we charge',
      ctx: 'Job P&L',
      d: 'What the customer will be billed, ex GST. Add up: every billable item at actual qty \u00d7 sales price (including labour copied from timesheets at the service rate), plus each billable PO recharge. Lines with Bill unticked contribute $0.',
      e: `Items ${fmt(itemsSale)} + PO recharge ${fmt(posSale)} = ${fmt(plCharge)}.`,
      f: 'Charge = \u03a3(actual qty \u00d7 sales price, billable) + \u03a3(PO ex-GST \u00d7 (1 + markup %), billable POs)',
      s: 'Billable items Amount column + PO sales amounts for job ' + jobRef + '.',
    },
    gst: {
      t: 'GST (10%)',
      ctx: 'Job P&L · margin strip',
      d: 'Goods & Services Tax added on top of the ex-GST sales amount.',
      e: `${fmt(plCharge)} \u00d7 10% = ${fmt(gst)}.`,
      f: 'GST = Sales amount (ex GST) \u00d7 10%',
      s: 'Derived from What we charge (ex GST) on this job P&L card.',
    },
    totalinc: {
      t: 'Total (inc GST)',
      ctx: 'Job P&L · margin strip',
      d: 'Final amount payable by the customer, GST included.',
      e: `${fmt(plCharge)} + ${fmt(gst)} = ${fmt(totalInc)}.`,
      f: 'Total = Sales amount (ex GST) + GST',
      s: 'Derived: Job P&L charge + GST row on this card.',
    },
    marginfo: {
      t: 'Margin',
      ctx: 'Job P&L · margin strip',
      d: 'Gross profit on the job — sales amount minus cost. Labour cost in the denominator uses timesheet total hours \u00d7 cost rate (all worked hours).',
      e: `${fmt(plCharge)} sales \u2212 ${fmt(plCost)} cost = ${fmt(margin)} (${marginPct.toFixed(1)}% of sales).`,
      f: 'Margin $ = Sales amount \u2212 Cost\nMargin % = Margin \u00f7 Sales amount',
      s: 'Derived from What we charge and What it costs on this job P&L.',
    },
    wipcost: {
      t: 'Cost backing this WIP',
      ctx: 'Job P&L · WIP card',
      d: 'The confirmed cost behind work not yet invoiced. Labour: timesheet total hours (every hour paid, including non-billable) \u00d7 service item cost rate. Materials: billable items at actual qty \u00d7 unit cost. POs: only where the supplier bill is reconciled — unconfirmed POs sit in Committed PO exposure instead.',
      e: `Total cost ${fmt(plCost)} \u2212 committed POs ${fmt(committedPO)} = ${fmt(wipCostBacking)}.`,
      f: 'Backing cost = \u03a3(timesheet total hours \u00d7 cost rate)\n  + \u03a3(actual qty \u00d7 unit cost)\n  + \u03a3(reconciled PO ex-GST)\n\n\u26a0 Cost uses timesheet total hours, NOT billable hours.',
      s: 'Labour: Timesheets \u2192 Timesheet.totalHours \u00d7 Service item.costRate (all hours on each row).\nMaterials: Billable items \u2192 actualQty \u00d7 Cost (Ex).\nPOs: Purchase Orders \u2192 PO.exGst WHERE linked Bill is Approved/Paid or receipts synced.',
    },
    poexp: {
      t: 'Committed PO exposure',
      ctx: 'Job P&L · WIP card',
      d: 'Purchase orders you have raised for this job where no supplier bill has been reconciled yet — money you are committed to paying but the final cost is not confirmed. This is exactly the set of POs that blocks "+ Sales Document".',
      e: committedPO > 0
        ? `${jobPOs.filter(p => !isReconciled(p)).map(p => p.id).join(', ')} = ${fmt(committedPO)} committed, bills not reconciled.`
        : 'Every PO on this job has a reconciled bill — exposure $0.00.',
      f: 'Exposure = \u03a3(PO ex-GST) WHERE no linked bill is Approved / Paid / receipts-synced',
      s: 'Purchase Orders → PO.exGst for job ' + jobRef + ' WHERE linked Bill is not Approved/Paid and receipts not synced.',
    },
    leak: {
      t: 'Unrecovered labour (leak)',
      ctx: 'Job P&L · WIP card',
      d: 'Hours you paid technicians for but are not charging the customer — travel, rework, non-billable time. Each row: (timesheet total hours \u2212 timesheet billable hours) \u00d7 service item cost rate.',
      e: tsRows.filter(r => r.hours > r.billableHrs)
        .map(r => `${r.staff}: (${r.hours.toFixed(2)} \u2212 ${r.billableHrs.toFixed(2)})h \u00d7 ${fmt(r.costRate)}`)
        .join(' + ') + ` = ${fmt(leak)}.`,
      f: 'Leak = \u03a3((timesheet total hours \u2212 timesheet billable hours) \u00d7 service item cost rate)',
      s: 'Timesheets on job ' + jobRef + ' \u2192 (Timesheet.totalHours \u2212 Timesheet.billableHours) \u00d7 Service item.costRate per row.',
    },
    costlab: {
      t: 'Labour cost',
      ctx: 'Job P&L · cost breakdown',
      d: 'Every timesheet row: timesheet total hours \u00d7 service item cost rate — all worked hours, including non-billable. Not billable hours. Not the service rate.',
      e: `${totalHrs.toFixed(2)} timesheet total hours across ${tsRows.length} row${tsRows.length !== 1 ? 's' : ''} = ${fmt(tsCost)}.`,
      f: 'Labour cost = \u03a3(timesheet total hours \u00d7 service item cost rate)\n\n\u26a0 Uses total hours, not billable hours.',
      s: 'Timesheets on job ' + jobRef + ' \u2192 Timesheet.totalHours \u00d7 Service item.costRate per row.',
    },
    costmat: {
      t: 'Materials & expenses cost',
      ctx: 'Job P&L · cost breakdown',
      d: 'Warehouse / van stock and expenses on the Items list, at actual qty \u00d7 unit cost. Service lines copied from timesheets are excluded here — their cost shows as "See timesheets" on the row and is counted once under Labour.',
      e: items.length > 0
        ? `${TM_EX.thrmCode} + ${TM_EX.calloutCode} on ${jobRef} = ${fmt(itemsCost)} materials & expense cost.`
        : `No van-stock HVAC parts on billable list — $0.00.`,
      f: 'Materials cost = \u03a3(actual qty \u00d7 unit cost)',
      s: 'Billable items · Product + Expense lines · Inventory purchase price \u00d7 actualQty.',
    },
    costpo: {
      t: 'Purchase orders cost',
      ctx: 'Job P&L · cost breakdown',
      d: 'The ex-GST cost of every purchase order raised against this job ref (Draft and Cancelled excluded) — reconciled or not.',
      e: jobPOs.length > 0
        ? `${jobPOs.map(p => `${p.id} ${p.title || p.reference || 'HVAC parts'}`).join(' + ')} = ${fmt(posCost)} ex-GST on this job.`
        : 'No HVAC supplier POs on this job yet — $0.00.',
      f: 'PO cost = \u03a3(PO ex-GST)',
      s: 'Purchase Orders module → PO.exGst WHERE jobRef = ' + jobRef + ' AND status \u2209 {Draft, Cancelled}.',
    },
    chglab: {
      t: 'Labour sales amount',
      ctx: 'Job P&L · charge breakdown',
      d: 'Service lines on the billable list (copied from timesheets): billable hours \u00d7 the service item\u2019s service rate. Only rows with Bill ticked count.',
      e: `Billable Service lines on ${jobRef} = ${fmt(labourCharge)} sales amount.`,
      f: 'Labour sales = \u03a3(timesheet billable hours \u00d7 service rate) on Service lines',
      s: 'Billable items \u2192 Type = Service \u2192 actualQty from Timesheet.billableHours \u00d7 Service item.salesRate.',
    },
    chgmat: {
      t: 'Materials & expenses sales amount',
      ctx: 'Job P&L · charge breakdown',
      d: 'Product and Expense lines on the billable list at actual qty \u00d7 sales price (ex GST). Only rows with Bill ticked count.',
      e: `Items sale ${fmt(itemsSale)} \u2212 labour ${fmt(labourCharge)} = ${fmt(materialsCharge)}.`,
      f: 'Materials sales = \u03a3(actual qty \u00d7 sales price)',
      s: 'Billable items · Product + Expense · actualQty \u00d7 Sales (Ex) from inventory price book.',
    },
    chgpo: {
      t: 'Purchase-order sales amount',
      ctx: 'Job P&L · charge breakdown',
      d: 'Each billable PO recharged to the customer: PO ex-GST cost \u00d7 (1 + the supplier\u2019s default markup %). Untick Bill on a PO line to exclude it.',
      e: `\u03a3(PO ex-GST \u00d7 markup) over billable POs = ${fmt(posSale)}.`,
      f: 'PO sales amount = \u03a3(PO ex-GST \u00d7 (1 + markup %))',
      s: 'Billable items · PO rows · PO.exGst from Purchase Orders \u00d7 Supplier.defaultMarkup%.',
    },
    markup: {
      t: 'Markup %',
      ctx: 'Job P&L · margin strip',
      d: 'Profit as a percentage of cost — how much you added on top of what the job costs you.',
      e: `${fmt(margin)} \u00f7 ${fmt(plCost)} = ${markupPct.toFixed(1)}%.`,
      f: 'Markup % = Margin \u00f7 Cost',
      s: 'Derived from Margin $ and What it costs on this job P&L.',
    },
    marginpct: {
      t: 'Margin %',
      ctx: 'Job P&L · margin strip',
      d: 'Profit as a percentage of the sales amount (what the customer pays ex GST).',
      e: `${fmt(margin)} \u00f7 ${fmt(plCharge)} = ${marginPct.toFixed(1)}%.`,
      f: 'Margin % = Margin \u00f7 Sales amount',
      s: 'Derived from Margin $ and What we charge on this job P&L.',
    },
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const copyToBillable = () => {
    const selected = tsRows.filter(r => r.copy);
    if (selected.length === 0) return;
    let nextId = items.reduce((m, l) => Math.max(m, l.id), 0);
    const newLines: ItemLine[] = selected.map(r => ({
      id: ++nextId, kind: 'Service', code: r.serviceCode,
      description: `${r.staff} — Daikin split system service labour`,
      reqQty: r.billableHrs, actualQty: r.billableHrs, cost: 0, sell: r.chargeRate, billable: true,
      tsLinked: true, tsCost: r.hours * r.costRate,
    }));
    setItems([...items, ...newLines]);
  };

  const addTimesheet = () => {
    const nextId = tsRows.reduce((m, r) => Math.max(m, r.id), 0) + 1;
    setTsRows([...tsRows, {
      id: nextId, staff: job?.technician || 'New tech', serviceCode: 'HVAC-SR',
      date: job?.visitDate || new Date().toLocaleDateString('en-AU'),
      start: '8 AM', end: '9 AM', hours: 1, billableHrs: 1, costRate: 65, chargeRate: 150, copy: false,
    }]);
  };

  const addItem = () => {
    const nextId = items.reduce((m, l) => Math.max(m, l.id), 0) + 1;
    setItems([...items, {
      id: nextId, kind: 'Product', code: '', description: 'New item',
      reqQty: 1, actualQty: 1, cost: 0, sell: 0, billable: true,
    }]);
  };

  const patchItem = (id: number, patch: Partial<ItemLine>) =>
    setItems(items.map(l => l.id === id ? { ...l, ...patch } : l));

  // ── Invoice gate + creation ────────────────────────────────────────────────
  const offendingPOs = jobPOs.filter(p => (poBillable[p.id] ?? true) && !isReconciled(p));
  const [gateOpen, setGateOpen] = useState(false);
  const [infoKey, setInfoKey] = useState<string | null>(null);

  const createSalesDocument = () => {
    if (offendingPOs.length > 0) { setGateOpen(true); return; }
    const invId = createInvoice({ ownerSalesRep: 'Paul Tyrrell' });
    updateInvoice(invId, {
      customerName: job?.customer || jobRef,
      customerAddress: job?.address,
      title: `Time & Materials — ${jobRef}`,
      purchaseOrderNumber: jobRef,
      salesTax, purchasesTax: purchTax,
    });
    const lines: InvoiceLineDetail[] = [
      ...items.filter(l => l.billable).map(l => ({
        invoiceId: invId,
        lineType: (l.kind === 'Service' ? 'service' : 'product') as InvoiceLineDetail['lineType'],
        inventoryCode: l.code || l.kind.toUpperCase(),
        description: l.description,
        qty: l.actualQty,
        purchasePrice: l.cost,
        markup: l.cost > 0 ? Math.round(((l.sell / l.cost) - 1) * 100) : 0,
        salesPrice: l.sell,
        discount: 0,
        taxable: true,
      })),
      ...jobPOs.filter(p => poBillable[p.id] ?? true).map(p => ({
        invoiceId: invId,
        lineType: 'product' as InvoiceLineDetail['lineType'],
        inventoryCode: p.id,
        description: `Materials on ${p.id} — ${p.title || p.reference}`,
        qty: 1,
        purchasePrice: p.exGst,
        markup: suppliers.find(s => s.id === p.supplierId)?.defaultMarkup ?? 20,
        salesPrice: poSell(p),
        discount: 0,
        taxable: true,
      })),
    ];
    updateInvoiceLineDetails(invId, lines);
    navigate(`/invoices/${invId}`);
  };

  const cellSx = { fontSize: '0.74rem', py: 0.6, whiteSpace: 'nowrap' as const, color: TM.inkSoft };
  const numSx = { ...cellSx, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };
  const headSx = { backgroundColor: '#fbfbfd', fontWeight: 800, fontSize: '0.62rem', color: TM.muted,
    textTransform: 'uppercase' as const, letterSpacing: 0.4, py: 0.7, whiteSpace: 'nowrap' as const };

  const billableCount = items.length + jobPOs.length;
  const allInfo = { ...INFO, ...PL_INFO, ...TS_COL_INFO, ...BI_COL_INFO };

  return (
    <div className="tm-workspace">
      <div className="tm-app">
        <JobEditChrome
          jobRef={jobRef}
          job={job}
          jobOptions={jobOptions}
          pricingMode={pricingMode}
          onPricingMode={setPricingMode}
          onJobRef={setJobRef}
          onOpenSettings={() => setSettingsOpen(true)}
          billableCount={billableCount}
        />

        <div className="tm-body">
          <div className="tm-taxrow">
            <span className="tm-taxlbl">Purchases are tax</span>
            <select className="tm-taxpill" value={purchTax} onChange={e => setPurchTax(e.target.value as TaxMode)}>
              <option value="Exclusive">Exclusive</option>
              <option value="Inclusive">Inclusive</option>
            </select>
            <span className="tm-taxlbl">Sales are tax</span>
            <select className="tm-taxpill" value={salesTax} onChange={e => setSalesTax(e.target.value as TaxMode)}>
              <option value="Exclusive">Exclusive</option>
              <option value="Inclusive">Inclusive</option>
            </select>
          </div>

      {/* ── 1 · Timesheets ─────────────────────────────────────────────────── */}
      <div id="tm-timesheets">
      <TmCollapsibleSection
        title="Timesheets"
        cost={tsCost}
        sale={tsSale}
        taxTag={purchTag}
        hours={{ totalHrs, billableHrs: totalBillableHrs }}
      >
        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TmColHead label="Copy" infoKey="ts_copy" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Staff" infoKey="ts_staff" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Service code" infoKey="ts_service" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Date" infoKey="ts_date" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Start" infoKey="ts_start" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="End" infoKey="ts_end" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Timesheet total hrs" infoKey="ts_totalhrs" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Timesheet billable hrs" infoKey="ts_billhrs" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Cost rate" infoKey="ts_costrate" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Cost" infoKey="ts_cost" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Service rate" infoKey="ts_servicerate" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Sale" infoKey="ts_sale" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {tsRows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={cellSx}>
                    <Checkbox size="small" checked={r.copy} sx={{ p: 0.3 }}
                      onChange={e => setTsRows(tsRows.map(x => x.id === r.id ? { ...x, copy: e.target.checked } : x))} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: TM.orange, fontWeight: 700 }}>{r.staff}</TableCell>
                  <TableCell sx={cellSx}>{r.serviceCode}</TableCell>
                  <TableCell sx={cellSx}>{r.date}</TableCell>
                  <TableCell sx={cellSx}>{r.start}</TableCell>
                  <TableCell sx={cellSx}>{r.end}</TableCell>
                  <TableCell sx={numSx}>{r.hours.toFixed(2)}</TableCell>
                  <TableCell sx={{ ...numSx, color: r.billableHrs < r.hours ? '#c62828' : undefined }}>
                    {r.billableHrs.toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ ...numSx, color: '#999' }}>{fmt(r.costRate)}</TableCell>
                  <TableCell sx={{ ...numSx, fontWeight: 700 }}>{fmt(r.hours * r.costRate)}</TableCell>
                  <TableCell sx={{ ...numSx, color: '#999' }}>{fmt(r.chargeRate)}</TableCell>
                  <TableCell sx={{ ...numSx, fontWeight: 700, color: BRAND }}>{fmt(r.billableHrs * r.chargeRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <div className="tm-btnrow">
          <button type="button" className="tm-btn purple" onClick={copyToBillable} disabled={!tsRows.some(r => r.copy)}>
            Copy to billable ({tsRows.filter(r => r.copy).length})
          </button>
          <button type="button" className="tm-btn ghost" onClick={addTimesheet}>+ Add timesheet</button>
        </div>
      </TmCollapsibleSection>
      </div>

      {/* ── 2 · Billable items ─────────────────────────────────────────────── */}
      <TmCollapsibleSection title="Billable items" cost={biCostDisplay} sale={biSale} taxTag={salesTag}>
        <div className="tm-bi-controls">
          <input
            className="tm-heading-in"
            placeholder="Add heading here..."
            value={sectionHeading}
            onChange={e => setSectionHeading(e.target.value)}
          />
          <div className="tm-bi-prices">
            <label>
              <span className="tm-bi-note" style={{ display: 'block', marginBottom: 4 }}>Purchases are tax</span>
              <select className="tm-taxsel" value={purchTax} onChange={e => setPurchTax(e.target.value as TaxMode)}>
                <option value="Exclusive">Exclusive</option>
                <option value="Inclusive">Inclusive</option>
              </select>
            </label>
            <label>
              <span className="tm-bi-note" style={{ display: 'block', marginBottom: 4 }}>Sales are tax</span>
              <select className="tm-taxsel" value={salesTax} onChange={e => setSalesTax(e.target.value as TaxMode)}>
                <option value="Exclusive">Exclusive</option>
                <option value="Inclusive">Inclusive</option>
              </select>
            </label>
          </div>
        </div>
        <p className="tm-bi-note" style={{ marginBottom: 10 }}>
          Prices are taken from your default inventory pricing. Purchase order lines are managed in Purchase Orders.
        </p>

        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TmColHead label="Bill" infoKey="bi_bill" onInfo={setInfoKey} sx={{ ...headSx, width: 48 }} />
                <TmColHead label="Type" infoKey="bi_type" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Code" infoKey="bi_code" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Description" infoKey="bi_desc" onInfo={setInfoKey} sx={headSx} />
                <TmColHead label="Required Qty" infoKey="bi_reqqty" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Actual Qty" infoKey="bi_actualqty" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Cost (Ex)" infoKey="bi_costex" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Sales (Ex)" infoKey="bi_salesex" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Cost total" infoKey="bi_costtotal" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TmColHead label="Amount" infoKey="bi_amount" onInfo={setInfoKey} align="right" sx={{ ...headSx, textAlign: 'right' }} />
                <TableCell sx={{ ...headSx, width: 40 }} />
              </TableRow>
            </TableHead>

            {/* ITEMS group */}
            <TableBody>
              <GroupHead label="Items (Product · Service · Expense)" infoKey="biitems"
                cost={itemsCostDisplay} sale={itemsSale} colSpan={9} onInfo={setInfoKey} />
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 1.4, textAlign: 'center', color: '#bbb', fontSize: '0.72rem' }}>
                    No items yet — add one, or copy billable hours from timesheets.
                  </TableCell>
                </TableRow>
              )}
              {items.map(l => (
                  <TableRow key={l.id} hover sx={{ opacity: l.billable ? 1 : 0.55 }}>
                    <TableCell sx={cellSx}>
                      <Checkbox size="small" checked={l.billable} sx={{ p: 0.3 }}
                        onChange={e => patchItem(l.id, { billable: e.target.checked })} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <select
                        className={`tm-kind-sel tm-typ ${KIND_CLASS[l.kind]}`}
                        value={l.kind}
                        onChange={e => {
                          const kind = e.target.value as ItemLine['kind'];
                          patchItem(l.id, kind === 'Service' ? { kind } : { kind, tsLinked: false, tsCost: 0 });
                        }}
                        aria-label="Line type"
                      >
                        {ITEM_KINDS.map(k => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TmCellInput
                        className="code"
                        value={l.code}
                        placeholder="Code"
                        onChange={v => patchItem(l.id, { code: v })}
                        aria-label="Code"
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, minWidth: 200 }}>
                      <TmCellInput
                        className="desc"
                        value={l.description}
                        onChange={v => patchItem(l.id, { description: v })}
                        aria-label="Description"
                      />
                    </TableCell>
                    <TableCell sx={numSx}>
                      <TmCellInput
                        type="number"
                        className="num"
                        value={l.reqQty}
                        onChange={v => patchItem(l.id, { reqQty: Number(v) || 0 })}
                        aria-label="Required qty"
                        style={{ width: 56 }}
                      />
                    </TableCell>
                    <TableCell sx={numSx}>
                      <TmCellInput
                        type="number"
                        className="num bold"
                        value={l.actualQty}
                        onChange={v => patchItem(l.id, { actualQty: Number(v) || 0 })}
                        aria-label="Actual qty"
                        style={{ width: 56 }}
                      />
                    </TableCell>
                    <TableCell sx={numSx}>
                      {l.tsLinked ? (
                        <button
                          type="button"
                          className="tm-tslink"
                          title="Labour cost comes from Timesheets — total hours × cost rate"
                          onClick={() => document.getElementById('tm-timesheets')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        >
                          See timesheets
                        </button>
                      ) : (
                        <TmCellInput
                          type="number"
                          className="num"
                          value={l.cost}
                          onChange={v => patchItem(l.id, { cost: Number(v) || 0 })}
                          aria-label="Cost ex"
                          style={{ width: 72 }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={numSx}>
                      <TmCellInput
                        type="number"
                        className="num"
                        value={l.sell}
                        onChange={v => patchItem(l.id, { sell: Number(v) || 0 })}
                        aria-label="Sales ex"
                        style={{ width: 72 }}
                      />
                    </TableCell>
                    <TableCell sx={numSx} title={l.tsLinked ? 'From Timesheets: total hours × cost rate' : undefined}>
                      {fmt(l.tsLinked ? (l.tsCost ?? 0) : l.actualQty * l.cost)}
                    </TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 700, color: BRAND }}>
                      {l.billable ? fmt(l.actualQty * l.sell) : fmt(0)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <IconButton size="small" onClick={() => setItems(items.filter(x => x.id !== l.id))}>
                        <DeleteOutlineIcon sx={{ fontSize: 15, color: '#c62828' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={11} sx={{ py: 0.6 }}>
                  <button type="button" className="tm-btn ghost" onClick={addItem} style={{ height: 28, fontSize: 12 }}>+ Item</button>
                </TableCell>
              </TableRow>
            </TableBody>

            {/* PURCHASE ORDERS group — real data */}
            {showPosInline && (
            <TableBody>
              <GroupHead label="Purchase orders" infoKey="bipo"
                cost={posCost} sale={posSale} colSpan={9} onInfo={setInfoKey} />
              {jobPOs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 1.4, textAlign: 'center', fontSize: '0.72rem' }}>
                    <Typography sx={{ color: '#bbb', mb: 1 }}>
                      No purchase orders reference job “{jobRef}”.
                    </Typography>
                    <button type="button" className="tm-btn purple" onClick={() => setPoDialogOpen(true)}>
                      + PO for this job
                    </button>
                  </TableCell>
                </TableRow>
              )}
              {jobPOs.map(po => {
                const recon = isReconciled(po);
                const billable = poBillable[po.id] ?? true;
                const tone = STATUS_TONE[getStatusTone(po.status)];
                return (
                  <TableRow key={po.id} hover
                    sx={{ backgroundColor: recon ? undefined : '#fff5f5', opacity: billable ? 1 : 0.55,
                      '& td:first-of-type': recon ? {} : { boxShadow: 'inset 3px 0 0 #c62828' } }}>
                    <TableCell sx={cellSx}>
                      <Checkbox size="small" checked={billable} sx={{ p: 0.3 }}
                        onChange={e => setPoBillable(prev => ({ ...prev, [po.id]: e.target.checked }))} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <span className="tm-typ po">PO</span>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <Typography
                          onClick={() => navigate(`/po-print/${po.id}`)}
                          sx={{ fontSize: '0.74rem', fontWeight: 800, color: BRAND, cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' } }}>
                          {po.id}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 11, color: '#bbb' }} />
                        <Chip label={po.status} size="small"
                          sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700,
                            backgroundColor: tone.bg, color: tone.fg, border: `1px solid ${tone.border}` }} />
                        {!recon && (
                          <Chip label="Not reconciled" size="small" onClick={() => setInfoKey('unrecon')}
                            sx={{ height: 17, fontSize: '0.58rem', fontWeight: 800, cursor: 'help',
                              backgroundColor: '#ffebee', color: '#b71c1c', border: '1.5px solid #e53935' }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cellSx}>{po.title || po.reference}</TableCell>
                    <TableCell sx={numSx}>1.00</TableCell>
                    <TableCell sx={numSx}>1.00</TableCell>
                    <TableCell sx={numSx}>{fmt(po.exGst)}</TableCell>
                    <TableCell sx={numSx}>{fmt(poSell(po))}</TableCell>
                    <TableCell sx={numSx}>{fmt(po.exGst)}</TableCell>
                    <TableCell sx={{ ...numSx, fontWeight: 700, color: BRAND }}>
                      {billable ? fmt(poSell(po)) : fmt(0)}
                    </TableCell>
                    <TableCell sx={cellSx} title="Driven by the PO — manage in Purchase Orders">
                      <LockIcon sx={{ fontSize: 13, color: '#c4c1cf' }} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {jobPOs.length > 0 && (
              <TableRow>
                <TableCell colSpan={11} sx={{ py: 0.6 }}>
                  <button type="button" className="tm-btn purple" onClick={() => setPoDialogOpen(true)} style={{ height: 28, fontSize: 12 }}>
                    + PO
                  </button>
                </TableCell>
              </TableRow>
              )}
            </TableBody>
            )}
          </Table>
        </Paper>

        <div className="tm-btnrow">
          <button type="button" className="tm-btn purple" onClick={addItem}>+ Section</button>
          {['Select items', 'Refresh pricing', 'Import CSV', 'Export CSV'].map(b => (
            <button key={b} type="button" className="tm-btn ghost">{b}</button>
          ))}
        </div>
      </TmCollapsibleSection>

      {/* ── 3 · Job P&L ────────────────────────────────────────────────────── */}
      <TmCollapsibleSection
        className="tm-sec-pnl"
        title={<>Job P&amp;L · {jobRef} <span className="badge">{pricingMode}</span></>}
        cost={plCost}
        sale={plCharge}
        taxTag={salesTag}
        defaultOpen
        footer={false}
      >
        <div className="tm-cards">
          <div className="tm-card">
            <div className="k">Work in progress</div>
            <div className="big">{fmt(wip)} <TmQ onClick={() => setInfoKey('wip')} /></div>
            <div className="sub">Cost not yet invoiced</div>
            <div className="div" />
            <div className="tm-row"><span>Cost backing this WIP <TmQ onClick={() => setInfoKey('wipcost')} /></span><span className="v">{fmt(wipCostBacking)}</span></div>
            <div className="tm-row warn-a"><span>Committed PO exposure <TmQ onClick={() => setInfoKey('poexp')} /></span><span className="v">{fmt(committedPO)}</span></div>
            <div className="tm-row warn-r"><span>Unrecovered labour (leak) <TmQ onClick={() => setInfoKey('leak')} /></span><span className="v">{fmt(leak)}</span></div>
          </div>

          <div className="tm-card">
            <div className="k">What it costs</div>
            <div className="big">{fmt(plCost)} <TmQ onClick={() => setInfoKey('cost')} /></div>
            <div className="div" />
            <div className="tm-row"><span>Labour <TmQ onClick={() => setInfoKey('costlab')} /></span><span className="v">{fmt(tsCost)}</span></div>
            <div className="tm-row"><span>Materials <TmQ onClick={() => setInfoKey('costmat')} /></span><span className="v">{fmt(itemsCost)}</span></div>
            <div className="tm-row"><span>Purchase orders <TmQ onClick={() => setInfoKey('costpo')} /></span><span className="v">{fmt(posCost)}</span></div>
          </div>

          <div className="tm-card">
            <div className="k">What we charge</div>
            <div className="big">{fmt(plCharge)} <TmQ onClick={() => setInfoKey('charge')} /></div>
            <div className="div" />
            <div className="tm-row"><span>Labour <TmQ onClick={() => setInfoKey('chglab')} /></span><span className="v">{fmt(labourCharge)}</span></div>
            <div className="tm-row"><span>Materials <TmQ onClick={() => setInfoKey('chgmat')} /></span><span className="v">{fmt(materialsCharge)}</span></div>
            <div className="tm-row"><span>Purchase orders <TmQ onClick={() => setInfoKey('chgpo')} /></span><span className="v">{fmt(posSale)}</span></div>
            <div className="tm-row faint" style={{ borderTop: '1px solid var(--tm-line)', marginTop: 6, paddingTop: 9 }}>
              <span>GST (10%) <TmQ onClick={() => setInfoKey('gst')} /></span><span className="v">{fmt(gst)}</span>
            </div>
            <div className="tm-row tot"><span>Total (inc GST) <TmQ onClick={() => setInfoKey('totalinc')} /></span><span className="v">{fmt(totalInc)}</span></div>
          </div>
        </div>

        <div className={`tm-marginbar${margin < 0 ? ' neg' : ''}`}>
          <div>
            <span className="ml">MARGIN</span>
            <span className="calc">= Sale {fmt(plCharge)} − Cost {fmt(plCost)}</span>
          </div>
          <div className="mr">
            <span className="markup">Markup {markupPct.toFixed(1)}% <TmQ onClick={() => setInfoKey('markup')} /></span>
            <span className="mval">{fmt(margin)} <TmQ onClick={() => setInfoKey('marginfo')} /></span>
            <span className="mpct">· {marginPct.toFixed(1)}% <TmQ onClick={() => setInfoKey('marginpct')} /></span>
          </div>
        </div>
      </TmCollapsibleSection>
        </div>
      </div>

      <TmActionsBar
        salesMenuAnchor={salesMenuAnchor}
        onSalesMenuAnchor={setSalesMenuAnchor}
        onCreateInvoice={createSalesDocument}
        onNavigateInvoice={id => navigate(`/invoices/${id}`)}
        onAllInvoices={() => navigate('/invoices-list')}
        jobInvoices={jobInvoices}
        offendingCount={offendingPOs.length}
        plCharge={plCharge}
        blocked={offendingPOs.length > 0}
      />

      <RaiseJobPoDialog
        open={poDialogOpen}
        jobRef={jobRef}
        jobTitle={job?.title}
        onClose={() => setPoDialogOpen(false)}
      />

      <InventorySettingsDrawer
        open={settingsOpen}
        jobRef={jobRef}
        showPosInline={showPosInline}
        onShowPosInline={setShowPosInline}
        onClose={() => setSettingsOpen(false)}
      />

      {/* ── Invoice gate dialog ────────────────────────────────────────────── */}
      <Dialog open={gateOpen} onClose={() => setGateOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Can’t invoice yet — unreconciled purchase orders</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.6, mb: 1.5 }}>
            {offendingPOs.length} billable purchase order{offendingPOs.length > 1 ? 's have' : ' has'} no
            approved supplier bill reconciled. The cost is committed but not confirmed — invoicing now risks
            charging the customer against a cost that may change.
          </Typography>
          {offendingPOs.map(po => (
            <Stack key={po.id} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
              <Chip label={po.status} size="small" sx={{ height: 17, fontSize: '0.58rem', fontWeight: 700 }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: BRAND }}>{po.id}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#888', flex: 1 }}>{fmt(po.exGst)} ex GST</Typography>
              <Button size="small" onClick={() => { setGateOpen(false); navigate(`/po-print/${po.id}`); }}
                sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, color: BRAND }}>
                Reconcile now
              </Button>
            </Stack>
          ))}
          <Typography sx={{ fontSize: '0.72rem', color: '#999', mt: 1.5 }}>
            Untick “Bill” on those lines to invoice without them, or reconcile each PO first.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGateOpen(false)} sx={{ textTransform: 'none', color: '#666' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <TmInfoModal infoKey={infoKey} entries={allInfo} onClose={() => setInfoKey(null)} />
    </div>
  );
};

export default TimeAndMaterialsPage;
